import {
  Button,
  DismissIcon,
  EyeIcon,
  Flex,
  HideEyeIcon,
  Link,
  NumberField,
  TextField,
  Typography,
  passwordRegex,
  telRegex,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'react-router';

import { useSignup } from '@jane/bloom/hooks/account/useSignup';
import { useFieldErrorVisibility } from '@jane/bloom/hooks/forms/useFieldErrorVisibility';
import type { Payload } from '@jane/bloom/routes/api.signup/types';
import { stricterEmailRegex } from '@jane/bloom/utils/strings/stricterEmailRegex';

import styles from './loginModal.module.css';

export interface SignUpProps {
  onClose: () => void;
  skipBagAfterAuth: boolean;
  userEmail: string;
}

export const SignUp = ({
  onClose,
  skipBagAfterAuth,
  userEmail,
}: SignUpProps) => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const { fetcher, signup } = useSignup({ skipBagAfterAuth });

  const {
    clearErrors,
    control,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setError,
  } = useForm<Payload>({
    defaultValues: {
      email: userEmail ?? '',
      password: '',
      phoneNumber: '',
    },
    mode: 'all',
  });

  const { getShowError, setShowError } = useFieldErrorVisibility<Payload>(
    [
      { clearRootError: true, id: 'email' },
      { id: 'phoneNumber' },
      { id: 'password' },
    ],
    (field) => clearErrors(field)
  );

  const signUpSubmit = ({ email, password, phoneNumber }: Payload) => {
    signup({ email, password, phoneNumber });
  };

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (!fetcher.data.success) {
        if (fetcher.data.errors['email']) {
          if (
            fetcher.data.errors['email'][0].includes('has already been taken')
          ) {
            setError('root', {
              message: `This email's already linked to an account. Try signing in instead.`,
            });
          } else {
            setError('email', {
              message: `Email ${fetcher.data.errors['email'][0]}`,
            });
          }
          setShowError('email', true);
        }

        if (fetcher.data.errors['phone']) {
          setError('phoneNumber', {
            message: `Phone ${fetcher.data.errors['phone'][0]}`,
          });
          setShowError('phoneNumber', true);
        }

        if (fetcher.data.errors['password']) {
          setError('password', {
            message: `Password ${fetcher.data.errors['password'][0]}`,
          });
          setShowError('password', true);
        }
      }
    }
  }, [fetcher.state, fetcher.data, setError, setShowError]);

  return (
    <Flex flexDirection="column" alignItems="center">
      <Button.Icon
        className={styles['closeButton']}
        icon={<DismissIcon />}
        label="close"
        onClick={onClose}
      />
      <Flex flexDirection="column" alignItems="center" px={24} pb={24}>
        <Typography variant="header" textAlign="center" mb={24}>
          Create your account
        </Typography>

        <Form
          name="Account Sign Up Form"
          className={styles['signUpForm']}
          onSubmit={handleSubmit(signUpSubmit)}
        >
          <div className={styles['signUpInputs']}>
            <Controller
              control={control}
              name="email"
              rules={{
                pattern: {
                  message: `Looks like the email isn't quite right.`,
                  value: stricterEmailRegex,
                },
                required: 'Please fill out this required field.',
              }}
              render={({ field }) => (
                <div className={styles['formField']}>
                  <TextField
                    disableMobileInputStyling
                    label="Email"
                    name="email"
                    width="100%"
                    placeholder="Enter your email"
                    defaultValue={userEmail}
                    className={clsx({
                      [styles['errorPassTextField']]:
                        getShowError('email') &&
                        errors.email &&
                        getValues('email') !== '',
                    })}
                    onChange={(email) => {
                      field.onChange(email);
                    }}
                    onBlur={() => {
                      setShowError('email', true);
                    }}
                  />
                  {errors.email && getShowError('email') && (
                    <Typography variant="body" color="error" mt={8}>
                      {errors.email?.message}
                    </Typography>
                  )}
                </div>
              )}
            />
            <Controller
              control={control}
              name="phoneNumber"
              rules={{
                pattern: {
                  message: `That number doesn't seem valid.`,
                  value: telRegex,
                },
                required: `Please fill out this required field.`,
              }}
              render={({ field }) => (
                <div className={styles['formField']}>
                  <NumberField
                    disableMobileInputStyling
                    allowLeadingZeros
                    autocomplete="off"
                    label="Phone"
                    maskFormat="(###) ###-####"
                    placeholder="Enter your phone"
                    className={clsx({
                      [styles['errorPassTextField']]:
                        getShowError('phone') &&
                        errors.phoneNumber &&
                        getValues('phoneNumber') !== '',
                    })}
                    onChange={(phone) => {
                      field.onChange(phone);
                    }}
                    onBlur={() => {
                      setShowError('phone', true);
                    }}
                    name="phone"
                    width="100%"
                  />
                  {errors.phoneNumber && getShowError('phoneNumber') && (
                    <Typography variant="body" color="error" mt={8}>
                      {errors.phoneNumber?.message}
                    </Typography>
                  )}
                  <Typography color="grays-mid" mt={8}>
                    Used only for SMS status updates
                  </Typography>
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name="password"
            rules={{
              pattern: {
                message: 'Your password must be at least 10 characters.',
                value: passwordRegex,
              },
              required: 'Please fill out this required field.',
            }}
            render={({ field }) => (
              <TextField
                disableMobileInputStyling
                type={isPasswordVisible ? 'text' : 'password'}
                label="Password"
                name="password"
                width="100%"
                placeholder="Enter your password"
                className={clsx({
                  [styles['errorPassTextField']]:
                    getShowError('password') && errors.password,
                })}
                onChange={(password) => {
                  field.onChange(password);
                }}
                onBlur={() => {
                  setShowError('password', true);
                }}
                endIcon={
                  isPasswordVisible ? (
                    <EyeIcon
                      onClick={() => setPasswordVisible(false)}
                      altText="show password"
                    />
                  ) : (
                    <HideEyeIcon
                      onClick={() => setPasswordVisible(true)}
                      altText="hide password"
                    />
                  )
                }
              />
            )}
          />

          {errors.password && getShowError('password') && (
            <Typography variant="body" color="error" mt={8}>
              {errors.password?.message}
            </Typography>
          )}
          {!(errors.password && getShowError('password')) && (
            <Typography color="grays-mid" mt={8}>
              Your password must be at least 10 characters.
            </Typography>
          )}

          <Button
            ariaLabel="Sign up button"
            label="Create account"
            full
            mt={24}
            loading={
              fetcher.state === 'loading' || fetcher.state === 'submitting'
            }
            type="submit"
            disabled={!isValid}
            variant="primary"
          />
        </Form>
        <Flex
          flexDirection="column"
          alignItems="center"
          className={styles['termsContainer']}
        >
          {errors.root && getShowError('email') && (
            <Typography variant="body" color="error" textAlign="center">
              {errors.root.message}
            </Typography>
          )}
          <Typography textAlign="center" color="grays-mid" mt={8}>
            By creating an account, you agree to Jane&#39;s{' '}
            <Link href="https://www.iheartjane.com/terms" color="grays-mid">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              href="https://www.iheartjane.com/privacy-policy"
              color="grays-mid"
            >
              Privacy Policy
            </Link>
            .
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
